import { mat4, vec2, vec3 } from 'gl-matrix'
import { Structure } from './Structure.js'
import type { BlockPos } from './core/BlockPos.js'
import type { StructureProvider } from './core/StructureProvider.js'
import { StructureRenderer } from './StructureRenderer.js'
import type { Resources } from './StructureRenderer.js'
import { Mesh } from './render/Mesh.js'
import { decodePickIdToBlockPos, unpackPickIdFromRgba } from './Picking.js'
import type { RayTraceHit, RayTraceRay } from './RayTrace.js'

export interface StructureViewportOptions {
	grid?: boolean
	chunkSize?: number
	clearColor?: [number, number, number, number]
	fov?: number
	minDistance?: number
	maxDistance?: number
	pickOnClick?: boolean
}

export class StructureViewport {
	private static readonly CLICK_PICK_THRESHOLD = 4

	public readonly canvas: HTMLCanvasElement
	public readonly gl: WebGL2RenderingContext
	public readonly renderer: StructureRenderer

	private structure: StructureProvider = new Structure([0, 0, 0])
	private frame: number | undefined
	private dragging = false
	private dragButton = 0
	private dragPos: [number, number] = [0, 0]
	private dragStartPos: [number, number] = [0, 0]
	private readonly cPos = vec3.create()
	private readonly cRot = vec2.fromValues(0.4, 0.6)
	private cDist = 10
	private gridVisible: boolean
	private overlayMesh: Mesh | null = null
	private readonly clearColor: [number, number, number, number]
	private readonly fov: number
	private readonly minDistance: number
	private readonly maxDistance: number
	private readonly pickOnClick: boolean
	private pickingFramebuffer: WebGLFramebuffer | null = null
	private pickingTexture: WebGLTexture | null = null
	private pickingDepthBuffer: WebGLRenderbuffer | null = null
	private pickingWidth = 0
	private pickingHeight = 0

	constructor(
		target: HTMLElement | HTMLCanvasElement,
		resources: Resources,
		options: StructureViewportOptions = {},
	) {
		this.canvas = target instanceof HTMLCanvasElement ? target : document.createElement('canvas')
		if (!(target instanceof HTMLCanvasElement)) {
			target.append(this.canvas)
		}
		this.canvas.classList.add('mcrender-viewport')
		const gl = this.canvas.getContext('webgl2', { antialias: false })
		if (!gl) {
			throw new Error('MCRender requires a browser with WebGL2 support.')
		}
		this.gl = gl
		this.renderer = new StructureRenderer(gl, this.structure, resources, {
			chunkSize: options.chunkSize ?? 16,
			useInvisibleBlockBuffer: false,
		})
		this.gridVisible = options.grid ?? true
		this.clearColor = options.clearColor ?? [0.08, 0.09, 0.1, 1]
		this.fov = options.fov ?? 55
		this.minDistance = options.minDistance ?? 1
		this.maxDistance = options.maxDistance ?? 500
		this.pickOnClick = options.pickOnClick ?? true
		this.bindEvents()
		this.requestRender()
	}

	public setStructure(structure: StructureProvider) {
		this.structure = structure
		this.renderer.setStructure(structure)
		const size = structure.getSize()
		vec3.set(this.cPos, -size[0] / 2, -size[1] / 2, -size[2] / 2)
		this.cDist = Math.max(4, vec3.length(vec3.fromValues(size[0], size[1], size[2])) * 1.2)
		this.requestRender()
	}

	public setGridVisible(visible: boolean) {
		this.gridVisible = visible
		this.requestRender()
	}

	public setOverlayMesh(mesh: Mesh | null) {
		this.overlayMesh = mesh
		this.requestRender()
	}

	public setPickedBlock(pos: BlockPos | null) {
		if (pos === null) {
			this.setOverlayMesh(null)
			return
		}
		this.setOverlayMesh(new Mesh().addLineCube(
			pos[0],
			pos[1],
			pos[2],
			pos[0] + 1,
			pos[1] + 1,
			pos[2] + 1,
			[1, 1, 1],
		))
	}

	public rayTrace(ray: RayTraceRay): RayTraceHit | null {
		return this.renderer.rayTrace(ray)
	}

	public resize() {
		const ratio = window.devicePixelRatio || 1
		const width = Math.max(1, Math.floor(this.canvas.clientWidth * ratio))
		const height = Math.max(1, Math.floor(this.canvas.clientHeight * ratio))
		if (this.canvas.width !== width || this.canvas.height !== height) {
			this.canvas.width = width
			this.canvas.height = height
			this.renderer.setViewport(0, 0, width, height)
		}
	}

	public pickBlockAt(clientX: number, clientY: number): BlockPos | null {
		this.resize()
		const rect = this.canvas.getBoundingClientRect()
		if (rect.width <= 0 || rect.height <= 0) {
			return null
		}
		const x = Math.floor((clientX - rect.left) * this.canvas.width / rect.width)
		const y = Math.floor((clientY - rect.top) * this.canvas.height / rect.height)
		return this.pickBlockAtCanvasPixel(x, y)
	}

	public pickBlockAtCanvasPixel(x: number, y: number): BlockPos | null {
		this.resize()
		const projMatrix = this.getProjectionMatrix(this.canvas.width, this.canvas.height)
		return this.pickBlockWithCamera(
			this.getViewMatrix(),
			projMatrix,
			this.canvas.width,
			this.canvas.height,
			x,
			y,
		)
	}

	public pickBlockWithCamera(
		viewMatrix: mat4,
		projMatrix: mat4,
		width: number,
		height: number,
		x: number,
		y: number,
	): BlockPos | null {
		const pixelX = Math.floor(x)
		const pixelY = Math.floor(y)
		if (
			width <= 0 || height <= 0 ||
			pixelX < 0 || pixelX >= width ||
			pixelY < 0 || pixelY >= height
		) {
			return null
		}

		this.ensurePickingFramebuffer(width, height)
		this.gl.bindFramebuffer(this.gl.FRAMEBUFFER, this.pickingFramebuffer)
		this.renderer.setViewport(0, 0, width, height)

		const pixel = new Uint8Array(4)
		try {
			this.gl.clearColor(0, 0, 0, 0)
			this.gl.clear(this.gl.COLOR_BUFFER_BIT | this.gl.DEPTH_BUFFER_BIT)
			this.renderer.drawPickingStructure(viewMatrix, projMatrix)
			this.gl.readPixels(
				pixelX,
				height - 1 - pixelY,
				1,
				1,
				this.gl.RGBA,
				this.gl.UNSIGNED_BYTE,
				pixel,
			)
		} finally {
			this.gl.bindFramebuffer(this.gl.FRAMEBUFFER, null)
			this.renderer.setViewport(0, 0, this.canvas.width, this.canvas.height)
		}

		return decodePickIdToBlockPos(unpackPickIdFromRgba(pixel), this.structure.getSize())
	}

	public requestRender() {
		if (this.frame !== undefined) {
			return
		}
		this.frame = requestAnimationFrame(() => {
			this.frame = undefined
			this.draw()
		})
	}

	public destroy() {
		if (this.frame !== undefined) {
			cancelAnimationFrame(this.frame)
			this.frame = undefined
		}
		this.canvas.removeEventListener('pointerdown', this.onPointerDown)
		this.canvas.removeEventListener('pointermove', this.onPointerMove)
		this.canvas.removeEventListener('pointerup', this.onPointerUp)
		this.canvas.removeEventListener('pointercancel', this.onPointerCancel)
		this.canvas.removeEventListener('wheel', this.onWheel)
		window.removeEventListener('resize', this.onResize)
		this.deletePickingFramebuffer()
	}

	private draw() {
		this.resize()
		this.gl.clearColor(...this.clearColor)
		this.gl.clear(this.gl.COLOR_BUFFER_BIT | this.gl.DEPTH_BUFFER_BIT)
		const viewMatrix = this.getViewMatrix()
		const projMatrix = this.getProjectionMatrix(this.canvas.width, this.canvas.height)
		if (this.gridVisible) {
			this.renderer.drawGrid(viewMatrix, projMatrix)
		}
		this.renderer.drawStructure(viewMatrix, projMatrix)
		if (this.overlayMesh !== null) {
			this.renderer.drawLineMesh(viewMatrix, projMatrix, this.overlayMesh)
		}
	}

	private getViewMatrix() {
		const viewMatrix = mat4.create()
		mat4.translate(viewMatrix, viewMatrix, [0, 0, -this.cDist])
		mat4.rotateX(viewMatrix, viewMatrix, this.cRot[1])
		mat4.rotateY(viewMatrix, viewMatrix, this.cRot[0])
		mat4.translate(viewMatrix, viewMatrix, this.cPos)
		return viewMatrix
	}

	private getProjectionMatrix(width: number, height: number) {
		const projMatrix = mat4.create()
		mat4.perspective(projMatrix, this.fov * Math.PI / 180, width / height, 0.1, 2000)
		return projMatrix
	}

	private ensurePickingFramebuffer(width: number, height: number) {
		if (
			this.pickingFramebuffer !== null &&
			this.pickingTexture !== null &&
			this.pickingDepthBuffer !== null &&
			this.pickingWidth === width &&
			this.pickingHeight === height
		) {
			return
		}

		if (this.pickingFramebuffer === null) {
			this.pickingFramebuffer = this.gl.createFramebuffer()
		}
		if (this.pickingTexture === null) {
			this.pickingTexture = this.gl.createTexture()
		}
		if (this.pickingDepthBuffer === null) {
			this.pickingDepthBuffer = this.gl.createRenderbuffer()
		}
		if (
			this.pickingFramebuffer === null ||
			this.pickingTexture === null ||
			this.pickingDepthBuffer === null
		) {
			throw new Error('Failed to create GPU picking framebuffer resources')
		}

		this.pickingWidth = width
		this.pickingHeight = height

		this.gl.bindTexture(this.gl.TEXTURE_2D, this.pickingTexture)
		this.gl.texImage2D(
			this.gl.TEXTURE_2D,
			0,
			this.gl.RGBA8,
			width,
			height,
			0,
			this.gl.RGBA,
			this.gl.UNSIGNED_BYTE,
			null,
		)
		this.gl.texParameteri(this.gl.TEXTURE_2D, this.gl.TEXTURE_MIN_FILTER, this.gl.NEAREST)
		this.gl.texParameteri(this.gl.TEXTURE_2D, this.gl.TEXTURE_MAG_FILTER, this.gl.NEAREST)
		this.gl.texParameteri(this.gl.TEXTURE_2D, this.gl.TEXTURE_WRAP_S, this.gl.CLAMP_TO_EDGE)
		this.gl.texParameteri(this.gl.TEXTURE_2D, this.gl.TEXTURE_WRAP_T, this.gl.CLAMP_TO_EDGE)

		this.gl.bindRenderbuffer(this.gl.RENDERBUFFER, this.pickingDepthBuffer)
		this.gl.renderbufferStorage(this.gl.RENDERBUFFER, this.gl.DEPTH_COMPONENT16, width, height)

		this.gl.bindFramebuffer(this.gl.FRAMEBUFFER, this.pickingFramebuffer)
		this.gl.framebufferTexture2D(
			this.gl.FRAMEBUFFER,
			this.gl.COLOR_ATTACHMENT0,
			this.gl.TEXTURE_2D,
			this.pickingTexture,
			0,
		)
		this.gl.framebufferRenderbuffer(
			this.gl.FRAMEBUFFER,
			this.gl.DEPTH_ATTACHMENT,
			this.gl.RENDERBUFFER,
			this.pickingDepthBuffer,
		)
		const status = this.gl.checkFramebufferStatus(this.gl.FRAMEBUFFER)
		this.gl.bindFramebuffer(this.gl.FRAMEBUFFER, null)
		this.gl.bindTexture(this.gl.TEXTURE_2D, null)
		this.gl.bindRenderbuffer(this.gl.RENDERBUFFER, null)
		if (status !== this.gl.FRAMEBUFFER_COMPLETE) {
			throw new Error(`GPU picking framebuffer is incomplete: ${status}`)
		}
	}

	private deletePickingFramebuffer() {
		if (this.pickingFramebuffer !== null) {
			this.gl.deleteFramebuffer(this.pickingFramebuffer)
			this.pickingFramebuffer = null
		}
		if (this.pickingTexture !== null) {
			this.gl.deleteTexture(this.pickingTexture)
			this.pickingTexture = null
		}
		if (this.pickingDepthBuffer !== null) {
			this.gl.deleteRenderbuffer(this.pickingDepthBuffer)
			this.pickingDepthBuffer = null
		}
		this.pickingWidth = 0
		this.pickingHeight = 0
	}

	private bindEvents() {
		this.canvas.addEventListener('pointerdown', this.onPointerDown)
		this.canvas.addEventListener('pointermove', this.onPointerMove)
		this.canvas.addEventListener('pointerup', this.onPointerUp)
		this.canvas.addEventListener('pointercancel', this.onPointerCancel)
		this.canvas.addEventListener('wheel', this.onWheel, { passive: false })
		this.canvas.addEventListener('contextmenu', evt => evt.preventDefault())
		window.addEventListener('resize', this.onResize)
	}

	private readonly onPointerDown = (evt: PointerEvent) => {
		this.dragging = true
		this.dragButton = evt.button
		this.dragPos = [evt.clientX, evt.clientY]
		this.dragStartPos = [evt.clientX, evt.clientY]
		this.canvas.setPointerCapture(evt.pointerId)
	}

	private readonly onPointerMove = (evt: PointerEvent) => {
		if (!this.dragging) {
			return
		}
		const dx = (evt.clientX - this.dragPos[0]) / 100
		const dy = (evt.clientY - this.dragPos[1]) / 100
		if (this.dragButton === 0) {
			vec2.add(this.cRot, this.cRot, [dx, dy])
			this.cRot[0] = this.cRot[0] % (Math.PI * 2)
			this.cRot[1] = Math.max(-Math.PI / 2, Math.min(Math.PI / 2, this.cRot[1]))
		} else if (this.dragButton === 1 || this.dragButton === 2) {
			vec3.rotateY(this.cPos, this.cPos, [0, 0, 0], this.cRot[0])
			vec3.rotateX(this.cPos, this.cPos, [0, 0, 0], this.cRot[1])
			const pan = vec3.fromValues(dx, -dy, 0)
			vec3.scale(pan, pan, 0.25 * this.cDist)
			vec3.add(this.cPos, this.cPos, pan)
			vec3.rotateX(this.cPos, this.cPos, [0, 0, 0], -this.cRot[1])
			vec3.rotateY(this.cPos, this.cPos, [0, 0, 0], -this.cRot[0])
		}
		this.dragPos = [evt.clientX, evt.clientY]
		this.requestRender()
	}

	private readonly onPointerUp = (evt: PointerEvent) => {
		if (
			this.dragging &&
			this.pickOnClick &&
			this.dragButton === 0 &&
			this.isClickPick(evt.clientX, evt.clientY)
		) {
			this.setPickedBlock(this.pickBlockAt(evt.clientX, evt.clientY))
		}
		this.dragging = false
		if (this.canvas.hasPointerCapture(evt.pointerId)) {
			this.canvas.releasePointerCapture(evt.pointerId)
		}
	}

	private readonly onPointerCancel = (evt: PointerEvent) => {
		this.dragging = false
		if (this.canvas.hasPointerCapture(evt.pointerId)) {
			this.canvas.releasePointerCapture(evt.pointerId)
		}
	}

	private isClickPick(clientX: number, clientY: number) {
		const dx = clientX - this.dragStartPos[0]
		const dy = clientY - this.dragStartPos[1]
		const threshold = StructureViewport.CLICK_PICK_THRESHOLD
		return dx * dx + dy * dy <= threshold * threshold
	}

	private readonly onWheel = (evt: WheelEvent) => {
		evt.preventDefault()
		this.cDist += evt.deltaY / 100
		this.cDist = Math.max(this.minDistance, Math.min(this.maxDistance, this.cDist))
		this.requestRender()
	}

	private readonly onResize = () => {
		this.requestRender()
	}
}
